/**
* Prescription View popup UI screen and logic
**/
import { Modal, Button } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import ApiService from './../../services/ApiService';
import MessageModal from './../../shared/modals/MessageModal';
import Pagination from './../../shared/components/Pagination';
import { convertUTCtoLocal } from './../../services/UtilsService';
import { useTranslation } from 'react-i18next';
import jsPDF from 'jspdf';

function PrescriptionViewModal(props) {
	/**
	 * Initial state declaration
	 **/
	const [data, setData] = useState([]);
	const [count, setCount] = useState(0);
	const [request, setRequest] = useState({ page: 0, limit: 10 });
	const [modal, setModal] = useState({});
	const [isLoading, setIsLoading] = useState(true);
	const [imageModal, setImageModal] = useState({ show: false, imageUrl: '' });
	const role = Number(localStorage.getItem('role'));
	const { t } = useTranslation();
    const BaseUrl = process.env.REACT_APP_SERVERURL;

	// Component initialize function
	useEffect(() => {
		const fetchPrescriptionData = async () => {
			setIsLoading(true);
			try {
				// Process prescription items from the passed diagnosis data
				if (props.data && props.data.prescription_items) {
					const prescriptionItems = props.data.prescription_items.map(item => ({
						...item,
						main_problem: props.data.main_problem,
						createdAt: item.createdAt,
						photo: item.photo
					}));
					
					// Apply pagination
					const startIndex = request.page * request.limit;
					const endIndex = startIndex + request.limit;
					const paginatedData = prescriptionItems.slice(startIndex, endIndex);
					
					setData(paginatedData);
					setCount(prescriptionItems.length);
				}
			} catch (error) {
				console.error('Error processing prescription data:', error);
			} finally {
				setIsLoading(false);
			}
		};
		
		if (props.data.showPrescription) {
			fetchPrescriptionData();
		}
	}, [request, props.data]);

	/**
	 * To handle pagination event
	 * @data we get the user selected page Number
	 **/
	const handlePaginate = (data) => {
		setRequest(req => ({ ...req, page: data }));
	};

	// To close modal
	const handleClose = () => setModal({});

	// To close image modal
	const handleImageClose = () => setImageModal({ show: false, imageUrl: '' });

	// View prescription image
	const handleViewPrescription = (imageUrl) => {
		setImageModal({ show: true, imageUrl: imageUrl });
	};

	// Print prescription
	const handlePrintPrescription = (imageUrl) => {
		const iframe = document.createElement('iframe');
		iframe.style.display = 'none';
		document.body.appendChild(iframe);

		iframe.contentWindow.document.write(`
			<html>
			<head>
				<title>Print</title>
				<style>
				body { margin: 0; text-align: center; }
				img { max-width: 100%; }
				</style>
			</head>
			<body onload="window.focus(); window.print();">
				<img src="${process.env.REACT_APP_SERVERURL}/${imageUrl}" alt="Prescription" />
			</body>
			</html>
		`);

		iframe.contentWindow.document.close();

		// Clean up after printing
		iframe.onload = function () {
			setTimeout(() => document.body.removeChild(iframe), 1000);
		};
	};

	// Download prescription as PDF
	const handleDownloadPrescription = async (imageUrl, index) => {
		try {
			// Create a new jsPDF instance
			const pdf = new jsPDF();
			
			// Create full image URL
			const fullImageUrl = `${process.env.REACT_APP_SERVERURL}/${imageUrl}`;
			
			// Create an image element to load the prescription image
			const img = new Image();
			img.crossOrigin = 'Anonymous'; // Handle CORS if needed
			
			img.onload = function() {
				// Get PDF dimensions
				const pdfWidth = pdf.internal.pageSize.getWidth();
				const pdfHeight = pdf.internal.pageSize.getHeight();
				
				// Calculate image dimensions to fit in PDF while maintaining aspect ratio
				const imgWidth = img.width;
				const imgHeight = img.height;
				const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
				
				const finalWidth = imgWidth * ratio;
				const finalHeight = imgHeight * ratio;
				
				// Center the image on the page
				const x = (pdfWidth - finalWidth) / 2;
				const y = (pdfHeight - finalHeight) / 2;
				
				// Add image to PDF
				pdf.addImage(img, 'JPEG', x, y, finalWidth, finalHeight);
				
				// Save the PDF
				pdf.save(`${props.data.name}_${props.data.dob}_prescription.pdf`);
			};
			
			img.onerror = function() {
				console.error('Error loading image for PDF conversion');
				// Fallback to original download method
				const link = document.createElement('a');
				link.href = fullImageUrl;
				link.download = `${props.data.name}_${props.data.dob}_prescription.jpg`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			};
			
			// Load the image
			img.src = fullImageUrl;
			
		} catch (error) {
			console.error('Error creating PDF:', error);
			// Fallback to original download method
			const link = document.createElement('a');
			link.href = `${process.env.REACT_APP_SERVERURL}/${imageUrl}`;
			link.download = `${props.data.name}_${props.data.dob}_prescription.jpg`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	};

	return (
		<>
			<Modal
				show={props.data.showPrescription}
				onHide={props.handleClose}
				backdrop="static"
				keyboard={false}
				size="l"
				aria-labelledby="contained-modal-title-vcenter"
				centered
			>
				{/* Import MessageModal */}
				{Object.keys(modal).length !== 0 && <MessageModal data={modal} handleClose={handleClose} />}

				<Modal.Header closeButton className={`${role === 4 ? 'admin-modal-header' : ''}`}>
					<Modal.Title>
						<h5 className="modal-title text-capitalize" id="ViewPrescriptionModal">
							{props.data?.name}{t('common.possessive_suffix')} {t('modal.prescription')}
						</h5>
					</Modal.Title>
				</Modal.Header>
				
				<Modal.Body>
					<div className="row">
						<div className="col-xxl-12 mb-5">
							<div className="table-responsive">
								{/* Prescription List Table */}
								<table className="table table-borderless theme-table">
									<thead className="table-nowrap">
										<tr>
											<th className="text-center">{t('table.createdDate')}</th>
											<th className="text-center">{t('sidebar.medicalIssues')}</th>
											<th className="text-center">{t('modal.action')}</th>
										</tr>
									</thead>
									<tbody>
										{data && data.length !== 0 && data.map((item, i) => (
											<tr key={i} className="text-capitalize">
												<td className="text-center">{convertUTCtoLocal(new Date(item.createdAt).toUTCString())}</td>
												<td className="text-capitalize text-center">{item.main_problem}</td>
												<td className="text-center">
													<div className="d-flex justify-content-center gap-2">
														{/* View Icon */}
														<span 
															onClick={() => handleViewPrescription(item.photo)}
															style={{ cursor: 'pointer' }}
															title={t('modal.ViewPrescription')}
														>
															<i className={`fa-solid fa-eye ${role === 4 ? 'admin-table-link text-tmAquaDgreen' : 'text-primary'}`} />
														</span>
														
														{/* Print Icon */}
														<span 
															onClick={() => handlePrintPrescription(item.photo)}
															style={{ cursor: 'pointer' }}
															title={t('modal.PrintPrescription')}
														>
															<i className={`fa-solid fa-print ${role === 4 ? 'admin-table-link text-tmAquaDgreen' : 'text-primary'}`} />
														</span>
														
														{/* Download Icon */}
														<span 
															onClick={() => handleDownloadPrescription(item.photo, i)}
															style={{ cursor: 'pointer' }}
															title={t('modal.DownloadPrescription')}
														>
															<i className={`fa-solid fa-download ${role === 4 ? 'admin-table-link text-tmAquaDgreen' : 'text-primary'}`} />
														</span>
													</div>
												</td>
											</tr>
										))}
										{data.length === 0 && !isLoading && (
											<tr>
												<td colSpan="3" className="text-center text-capitalize">
													{t('common.noRecord')}
												</td>
											</tr>
										)}
									</tbody>
								</table>
								{/* End Prescription List Table */}
							</div>
						</div>
					</div>
					
					{/* Pagination */}
					<div className="row my-3">
						<div className="col-xxl-12 d-flex justify-content-center">
							{data.length !== 0 && (
								<Pagination 
									page={request.page} 
									handlePaginate={handlePaginate} 
									total={count} 
								/>
							)}
						</div>
					</div>
					{/* End Pagination */}
				</Modal.Body>
				
				{/* Footer */}
				<Modal.Footer>
					<Button 
						variant="secondary" 
						className={`btn btn-sm ${role === 4 ? 'btn-tmAquaGreen' : 'btn-tmBlue'}`} 
						onClick={props.handleClose}
					>
						{t('modal.close')}
					</Button>
				</Modal.Footer>
				{/* End Footer */}
			</Modal>

			{/* Image View Modal */}
			<Modal
				show={imageModal.show}
				onHide={handleImageClose}
				size="lg"
				centered
			>
				<Modal.Header closeButton className={`${role === 4 ? 'admin-modal-header' : ''}`}>
					<Modal.Title>
						<h5 className="modal-title">{t('modal.prescription')} {t('modal.image')}</h5>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body className="text-center">
					<img 
						src={`${BaseUrl}${imageModal.imageUrl}`} 
						alt="Prescription" 
						className="img-fluid"
						style={{ maxHeight: '250vh', objectFit: 'contain' }}
					/>
				</Modal.Body>
				<Modal.Footer>
					<Button 
						variant="secondary" 
						className={`btn btn-sm ${role === 4 ? 'btn-tmAquaGreen' : 'btn-tmBlue'}`} 
						onClick={handleImageClose}
					>
						{t('modal.close')}
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
}

export default PrescriptionViewModal;